/** English source catalog (source locale and fallback). Keys are stable English identifiers. */
export const en = {
  app: {
    name: 'Vammo Issue Tracker',
    fixtureBanner: 'Fixture environment — sample data, no live integrations, no real messages.',
    skipToContent: 'Skip to content',
  },
  nav: {
    myWork: 'My work',
    requests: 'Requests',
    work: 'Work',
    projects: 'Projects',
    teams: 'Teams',
    reports: 'Reports',
    admin: 'Administration',
    settings: 'Settings',
    newRequest: 'New request',
    newWork: 'New work item',
    search: 'Search',
    searchPlaceholder: 'Search work and requests or type a key…',
    notifications: 'Notifications',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    openMenu: 'Open navigation',
    signOut: 'Sign out',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    shortcuts: 'Keyboard shortcuts',
  },
  repoPicker: {
    workTitle: 'Linked repositories',
    workHint: 'Linking repositories adds evidence for future work. Completed episodes keep their original basis.',
    projectTitle: 'Project repositories',
    projectHint: 'Reconciliation and capture coverage include repositories attached here.',
    search: 'Search repositories',
    noneSelected: 'No repositories selected.',
    remove: 'Remove {{name}}',
    archived: 'Archived',
    edit: 'Edit repositories',
  },
  translationReport: {
    action: 'Report',
    title: 'Report this translation',
    hint: 'Tell us what is wrong. The original text is never changed.',
    comment: 'What is wrong? (optional)',
    regenerate: 'Translate it again',
    submit: 'Send report',
    thanks: 'Thanks, the report was recorded',
  },
  languageCorrection: {
    label: 'Original language',
    hint: 'Correct it if the text was detected in the wrong language; translations are recreated.',
  },
  conflict: {
    title: 'Someone else saved first',
    hint: 'Compare your change with the current version, then choose which one to keep.',
    field: 'Field',
    mine: 'Your change',
    current: 'Current version',
    applyMine: 'Apply my change',
    keepCurrent: 'Keep current version',
    fields: { title: 'Title', descriptionMarkdown: 'Description', acceptanceCriteria: 'Acceptance criteria', priority: 'Priority', assigneeId: 'Assignee', labels: 'Labels', type: 'Type', originalLocale: 'Original language', repositoryIds: 'Repositories', problemMarkdown: 'Problem', expectedOutcome: 'Expected outcome' },
  },
  common: {
    removeItem: 'Remove {{name}}',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    archive: 'Archive',
    confirm: 'Confirm',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    retry: 'Retry',
    reload: 'Reload',
    loading: 'Loading…',
    loadMore: 'Load more',
    none: 'None',
    unassigned: 'Unassigned',
    unknown: 'Unknown',
    optional: 'optional',
    required: 'Required',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    me: 'Me',
    filter: 'Filter',
    clearFilters: 'Clear filters',
    sort: 'Sort',
    openFullPage: 'Open full page',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    viewOriginal: 'View original',
    viewTranslation: 'View translation',
    machineTranslated: 'Machine translated from {{language}}',
    translating: 'Translating…',
    translationFailed: 'Translation unavailable; showing the original.',
    reportTranslation: 'Report translation',
    noResults: 'No results',
    comingSoon: 'Not available yet',
    reason: 'Reason',
    reasonPlaceholder: 'Explain the decision',
    created: 'Created',
    updated: 'Updated',
    by: 'by {{name}}',
    you: 'you',
    changesSaved: 'Changes saved',
    actionFailed: 'Action failed',
    count_one: '{{count}} item',
    count_other: '{{count}} items',
    preview: 'Preview',
    write: 'Write',
    markdownHint: 'Markdown supported: **bold**, _italic_, `code`, lists and links.',
    unsavedChanges: 'You have unsaved changes. Save or cancel them first.',
    discard: 'Discard',
    keepEditing: 'Keep editing',
  },
  errors: {
    title: 'Something went wrong',
    notFound: 'Not found, or you do not have access to it.',
    forbidden: 'You do not have permission to do that.',
    unauthenticated: 'Your session ended. Sign in again to continue.',
    conflict: 'Someone else changed this in the meantime. Review the latest version and reapply your edit.',
    staleGeneration: 'A newer delivery exists. Review the current state before responding.',
    rateLimited: 'Too many requests. Please wait a moment.',
    unavailable: 'This capability is not available right now.',
    offline: 'You appear to be offline. Changes will not be saved until the connection returns.',
    validation: 'Please correct the highlighted fields.',
    generic: 'Unexpected error. Reference: {{correlationId}}',
  },
  auth: {
    signIn: 'Sign in with Vammo',
    signingIn: 'Redirecting to sign in…',
    errorTitle: 'Sign-in did not complete',
    errorState: 'The sign-in link expired or was already used. Start again.',
    errorSuspended: 'Your account is suspended. Contact an administrator.',
    errorGeneric: 'We could not verify your corporate sign-in.',
    tryAgain: 'Try again',
  },
  status: {
    request: {
      DRAFT: 'Draft',
      SUBMITTED: 'Submitted',
      NEEDS_INFORMATION: 'Needs information',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      DUPLICATE: 'Duplicate',
      RESOLVED: 'Resolved',
      NEEDS_ATTENTION: 'Needs attention',
      CANCELED: 'Canceled',
    },
    phase: {
      TODO: 'To do',
      IN_PROGRESS: 'In progress',
      IN_REVIEW: 'In review',
      INTEGRATED: 'Integrated',
      DONE: 'Done',
      CANCELED: 'Canceled',
    },
    priority: { URGENT: 'Urgent', HIGH: 'High', NORMAL: 'Normal', LOW: 'Low' },
    workType: {
      FEATURE: 'Feature',
      BUG: 'Bug',
      MAINTENANCE: 'Maintenance',
      INFRASTRUCTURE: 'Infrastructure',
      RESEARCH: 'Research',
      ANALYSIS: 'Analysis',
      OPERATIONS: 'Operations',
      REVIEW: 'Review',
      OTHER: 'Other',
    },
    policy: { PRODUCTION: 'Production delivery', DELIVERABLE: 'Deliverable', REVIEWED_DELIVERABLE: 'Reviewed deliverable' },
    policyHelp: {
      PRODUCTION: 'Done only when every required change is verified in production.',
      DELIVERABLE: 'Done when the responsible person records the outcome against the criteria.',
      REVIEWED_DELIVERABLE: 'Done when an independent reviewer approves the outcome.',
    },
    delivery: { UNKNOWN: 'Delivery unknown', NOT_DEPLOYED: 'Not deployed', PARTIAL: 'Partially deployed', DEPLOYED: 'Deployed', REVERTED: 'Reverted' },
    association: { CONFIRMED: 'Confirmed', PROVISIONAL: 'Provisional' },
    visibility: { COMPANY: 'Company', RESTRICTED: 'Restricted' },
    visibilityHelp: { COMPANY: 'Visible to every signed-in Vammo employee.', RESTRICTED: 'Visible only to the selected people, teams and groups (and administrators).' },
    basis: { AGENT_REPORTED: 'Agent reported', HUMAN_REPORTED: 'Reported', PROVIDER_VERIFIED: 'Verified', IMPORTED_SOURCE: 'Imported', FIXTURE: 'Fixture' },
    completionBasis: { VERIFIED_PRODUCTION: 'Verified in production', DELIVERABLE: 'Deliverable recorded', REVIEWED_DELIVERABLE: 'Reviewed and approved', IMPORTED_SOURCE: 'Done in Jira (imported)' },
    confirmation: { UNCONFIRMED: 'Awaiting your confirmation', CONFIRMED: 'Confirmed resolved', PERSISTS: 'Still happening' },
    fulfillment: {
      NOT_STARTED: 'Not started',
      IN_PROGRESS: 'Being worked on',
      IN_REVIEW: 'In review',
      AWAITING_DEPLOYMENT: 'Awaiting production deployment',
      AWAITING_ACTIVATION: 'Awaiting activation',
      DELIVERED: 'Delivered',
      NONE_LINKED: 'Not yet planned',
    },
    channel: { WEB: 'Web', SLACK: 'Slack', MCP: 'AI assistant', API: 'API', EMAIL: 'Email', JIRA_IMPORT: 'Jira import', SYSTEM: 'System' },
    scope: { NONE: 'Not applicable', AWAITING_SCOPE: 'Awaiting delivery scope', UNCONFIRMED: 'Scope not confirmed', CONFIRMED: 'Scope confirmed' },
    milestone: { START: 'Started', SCOPE_CHANGE: 'Scope changed', BLOCKER: 'Blocker', VERIFICATION: 'Verification', PR_UPDATE: 'Pull request', HANDOFF: 'Handoff', SESSION_END: 'Session ended', NOTE: 'Note' },
    client: { CLAUDE_CODE: 'Claude Code', CODEX: 'Codex', CLAUDE: 'Claude', CHATGPT: 'ChatGPT', OTHER: 'Other client' },
  },
  actions: {
    request: {
      submit: 'Submit request',
      cancel: 'Cancel request',
      ask_information: 'Ask for information',
      answer_information: 'Send answer',
      accept: 'Accept',
      reject: 'Reject',
      mark_duplicate: 'Mark as duplicate',
      fulfill: 'Mark fulfilled',
      report_persistence: 'Report still happening',
      fulfillment_invalidated: 'Reopen for attention',
      accept_correction: 'Accept correction',
      fulfill_after_review: 'Resolve after review',
      reconsider: 'Reconsider',
    },
    work: {
      start: 'Start',
      request_review: 'Request review',
      request_changes: 'Request changes',
      integration_observed: 'Integrated',
      production_verified: 'Verified in production',
      complete_deliverable: 'Complete',
      approve_deliverable: 'Approve',
      cancel: 'Cancel work',
      reopen: 'Reopen',
      coverage_lost: 'Coverage lost',
    },
  },
  myWork: {
    title: 'My work',
    greeting: 'Hello, {{name}}',
    subtitle: 'What needs your attention today.',
    assigned: 'Assigned to me',
    assignedEmpty: 'Nothing assigned to you right now.',
    myRequests: 'My requests',
    myRequestsEmpty: 'You have not filed any requests yet.',
    needsMe: 'Waiting on me',
    needsMeEmpty: 'Nothing is waiting on you.',
    blocked: 'Blocked',
    triageInbox: 'Triage inbox',
    updates: 'Recent updates',
    updatesEmpty: 'No updates yet.',
    following: 'Following',
    followingEmpty: 'Follow work or requests to see them here.',
    stats: { open: 'Open work', inReview: 'In review', blocked: 'Blocked', attention: 'Requests needing attention' },
  },
  requests: {
    move: { noProject: 'No project', title: 'Move to {{name}}', hint: 'Moving changes who can read this request and its comments and files.', restrictedAfter: 'After the move the request will be restricted to the project audience.', companyAfter: 'After the move the request will be visible company-wide.', losesAccess: 'These people would lose access:', nobodyLoses: 'Nobody involved loses access.', reassignFirst: 'The assignee would lose access. Reassign the request first.', confirm: 'Move request' },
    activation: { pending: 'Activation pending:', observed: 'Activation observed by {{name}} on {{time}}', notRequired: 'No separate activation required.', require: 'Require activation', record: 'Record activation', waive: 'Waive activation', requireHint: 'Use this when shipping is not enough, for example when a feature flag must be turned on. The request stays accepted until activation is recorded.', recordHint: 'Record who observed the change live and the evidence. This is audited.', waiveHint: 'Activation will no longer be required. The reason is audited.', noteLabel: 'What must be activated?', evidenceLabel: 'Evidence', reasonLabel: 'Reason' },
    duplicates: { title: 'Possible duplicates', hint: 'Similar requests you can see. Marking keeps each requester informed.', mark: 'Mark as duplicate of this' },
    title: 'Requests',
    subtitle: 'Problems, ideas and asks from across Vammo.',
    tabs: { inbox: 'Inbox', mine: 'Mine', attention: 'Needs attention', following: 'Following', all: 'All' },
    empty: 'No requests here.',
    emptyInbox: 'The inbox is clear. New requests appear here for triage.',
    columns: { key: 'Key', title: 'Title', status: 'Status', priority: 'Priority', author: 'Requester', assignee: 'Assignee', team: 'Team', updated: 'Updated', source: 'Source' },
    detail: {
      problem: 'Problem',
      currentBehavior: 'What happens now',
      expectedOutcome: 'Expected outcome',
      impact: 'Impact',
      impactUnknown: 'Impact not known yet',
      reproduction: 'How to reproduce',
      affectedArea: 'Affected area',
      progress: 'Progress',
      decisions: 'Decision history',
      comments: 'Comments',
      linkedWork: 'Linked work',
      linkedWorkEmpty: 'No work linked yet.',
      required: 'Required',
      optional: 'Optional',
      hiddenWork: 'Some linked work is restricted and not shown.',
      pendingQuestion: 'The team asked',
      answerPlaceholder: 'Answer the question…',
      duplicateOf: 'Duplicate of',
      decisionReason: 'Decision note',
      source: 'Source',
      triage: 'Triage',
      triageTeam: 'Triage team',
      followers: 'Follow',
      following: 'Following',
      linkWork: 'Link work',
      linkWorkSearch: 'Search work by key or title',
      unlink: 'Unlink',
    },
    confirm: {
      fromEmail: 'Choose your answer below. Nothing is recorded until you click.',
      title: 'Was this resolved for you?',
      body: 'The team marked this request as resolved. Let them know if it works for you.',
      confirmed: 'Yes, it is resolved',
      persists: 'No, it is still happening',
      commentPlaceholder: 'Add details (optional)',
      thanks: 'Thanks for confirming.',
      reported: 'Thanks — the team will review it again.',
      generation: 'Delivery #{{generation}}',
    },
    dialogs: {
      rejectTitle: 'Reject request',
      duplicateTitle: 'Mark as duplicate',
      duplicateSearch: 'Find the canonical request',
      askTitle: 'Ask for information',
      askPlaceholder: 'What do you need to know?',
      fulfillTitle: 'Mark as fulfilled',
      fulfillDirect: 'Describe the delivered outcome',
      reviewTitle: 'Resolve after review',
      reviewPlaceholder: 'Explain why the current result now fulfills the request',
      correctionTitle: 'Accept correction',
      correctionWork: 'Corrective work item (optional)',
      cancelTitle: 'Cancel request',
      reconsiderTitle: 'Reconsider request',
    },
  },
  intake: {
    title: 'New request',
    subtitle: 'Describe what is happening. You do not need to know how to fix it.',
    tabs: { assistant: 'Guided', form: 'Form' },
    assistantIntro: 'Tell me about the problem or idea in your own words. I will ask only for what is missing.',
    assistantPlaceholder: 'Type your answer…',
    assistantUnavailable: 'The assistant is unavailable right now. Use the form; everything is saved.',
    assistant: {
      ack: 'Thanks, I saved that in the draft.',
      askProblem: 'What is happening, and where?',
      askExpected: 'What should happen instead?',
      askImpact: 'Who is affected, and how much does it matter? It is fine to say you do not know.',
      askReproduction: 'If it is a bug: how can someone see it happen, and how often?',
      ready: 'I have what triage needs. Review the summary and submit when it looks right.',
      fallback: 'The assistant is unavailable, so I saved your answer as-is.',
    },
    fields: {
      title: 'Short title',
      titlePlaceholder: 'e.g. Rider app does not show swap history',
      problem: 'What is the problem?',
      problemPlaceholder: 'Describe the situation and who is affected',
      currentBehavior: 'What happens now?',
      expectedOutcome: 'What should happen instead?',
      impact: 'Impact',
      impactPlaceholder: 'Who or what is affected, and how much?',
      impactUnknown: 'I do not know the impact yet',
      reproduction: 'Steps to reproduce (for bugs)',
      affectedArea: 'Affected area or product',
      language: 'Written in',
      visibility: 'Who can see this',
      team: 'Route to team',
      teamAuto: 'Let triage decide',
      priority: 'Priority',
      evidence: 'Attachments',
    },
    duplicates: 'Possibly related requests',
    duplicatesHelp: 'Check whether one of these already covers your request.',
    review: 'Review and submit',
    reviewHelp: 'Check the summary below. You can still edit before submitting.',
    missing: 'Still missing: {{fields}}',
    saved: 'Draft saved',
    submitted: 'Request {{key}} submitted',
    draftNote: 'Drafts are private until you submit them.',
  },
  work: {
    title: 'Work',
    subtitle: 'Everything in flight across teams, projects and repositories.',
    views: { list: 'List', board: 'Board' },
    empty: 'No work matches these filters.',
    columns: { key: 'Key', title: 'Title', phase: 'Phase', priority: 'Priority', type: 'Type', assignee: 'Assignee', project: 'Project', team: 'Team', updated: 'Updated', delivery: 'Delivery' },
    filters: { phase: 'Phase', type: 'Type', priority: 'Priority', assignee: 'Assignee', project: 'Project', team: 'Team', blocked: 'Blocked only', provisional: 'Provisional only', includeCanceled: 'Include canceled', label: 'Label' },
    bulk: { selected: '{{count}} selected', setPriority: 'Set priority', assign: 'Assign', addLabel: 'Add label', clear: 'Clear selection' },
    board: { moveTo: 'Move to…', dropRejected: 'That move is not allowed: {{reason}}', productionHint: 'Production work reaches Done only from verified deployments.' },
    create: {
      title: 'New work item',
      responsibility: 'Responsible',
      team: 'Team',
      personal: 'Personal (me)',
      project: 'Project',
      noProject: 'No project',
      type: 'Type',
      policy: 'Completion',
      description: 'Description',
      criteria: 'Acceptance criteria',
      addCriterion: 'Add criterion',
      criterionPlaceholder: 'Observable result…',
      assignee: 'Assignee',
      labels: 'Labels',
      labelsPlaceholder: 'comma, separated',
      created: '{{key}} created',
    },
    provisional: {
      confirm: 'Confirm as new work',
      merge: 'Merge into existing work',
      mergeTitle: 'Merge into existing work',
      mergeHint: 'The key becomes an alias of the chosen item. Contributions, sessions, pull requests and request links move with it; nothing is deleted.',
      mergeInto: 'Merge into {{key}}',
      confirmed: 'Confirmed as new work',
      merged: 'Merged into {{key}}',
    },
    detail: {
      overview: 'Overview',
      activity: 'Activity',
      progress: 'Progress',
      delivery: 'Delivery',
      history: 'History',
      description: 'Description',
      noDescription: 'No description yet.',
      criteria: 'Acceptance criteria',
      noCriteria: 'No acceptance criteria.',
      properties: 'Properties',
      owner: 'Owner',
      reviewer: 'Reviewer',
      repositories: 'Repositories',
      noRepositories: 'No repositories — that is fine for non-code work.',
      labels: 'Labels',
      blockers: 'Blockers',
      addBlocker: 'Add blocker',
      blockerReason: 'What is blocking?',
      preventsCompletion: 'Prevents completion',
      resolveBlocker: 'Resolve',
      relations: 'Related work',
      addRelation: 'Link work',
      relationKinds: { PARENT_OF: 'Parent of', DEPENDS_ON: 'Depends on', RELATES_TO: 'Relates to', DUPLICATES: 'Duplicates' },
      requiresCompletion: 'Required for completion',
      contributors: 'Contributors',
      linkedRequests: 'Requests',
      outcome: 'Outcome',
      outcomePlaceholder: 'What was delivered?',
      complete: 'Complete deliverable',
      criterionStatus: { OPEN: 'Open', MET: 'Met', NOT_MET: 'Not met', WAIVED: 'Waived' },
      episodes: 'Completion episodes',
      provisionalNote: 'Created provisionally by an agent; confirm or merge it.',
      blockedNote: 'Blocked',
      awaitingDeployment: 'Awaiting deployment verification',
      sessions: 'Agent sessions',
      evidence: 'Evidence',
      noEvidence: 'No evidence attached.',
      timelineEmpty: 'No history yet.',
    },
  },
  projects: {
    addManager: 'Add a manager',
    addTeam: 'Add an owning team',
    archive: 'Archive this project',
    archiveHint: 'Archived projects leave lists and pickers; their work and history stay readable.',
    title: 'Projects',
    subtitle: 'Projects group work; repositories are optional.',
    new: 'New project',
    empty: 'No projects yet.',
    key: 'Key',
    name: 'Name',
    description: 'Description',
    managers: 'Managers',
    owningTeams: 'Owning teams',
    openWork: '{{count}} open',
    defaultPolicy: 'Default completion',
    requiresReview: 'Require independent review for deliverables',
    settings: 'Settings',
    visibility: 'Visibility',
    repositories: 'Repositories',
  },
  teams: {
    title: 'Teams',
    members: 'Members',
    manager: 'Manager',
    member: 'Member',
    addMember: 'Add member',
    remove: 'Remove',
    new: 'New team',
    slug: 'Identifier',
  },
  visibility: {
    title: 'Visibility',
    grants: 'People, teams and groups',
    addGrant: 'Add access',
    searchPrincipal: 'Search people, teams or groups',
    inherit: 'Restrictions from parent projects always apply.',
    changed: 'Visibility updated',
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    displayName: 'Display name',
    language: 'Language',
    timezone: 'Time zone',
    notifications: 'Notifications',
    identities: 'Linked accounts',
    noIdentities: 'No linked accounts.',
    roles: 'Access',
    basic: 'Basic access',
    github: {
      addLabel: 'Add a GitHub account',
      hint: 'Your GitHub username, for example a personal account you sometimes commit with. Accounts that commit with your Vammo email are linked automatically.',
      placeholder: 'username',
      add: 'Add',
      added: 'GitHub account added',
      remove: 'Remove',
      removed: 'GitHub account removed',
      confirmRemove: 'Remove this account?',
      method: { self: 'Added by you', email: 'Verified by commit email', admin: 'Linked by an administrator', other: 'Linked' },
      errors: {
        notFound: 'No GitHub account has this username.',
        notUser: 'Only personal GitHub accounts can be added, not organizations or bots.',
        taken: 'This GitHub account is already linked to someone else. Ask an administrator.',
        tooMany: 'You already have the maximum number of GitHub accounts.',
        unavailable: 'GitHub could not be reached. Try again in a moment.',
      },
    },
  },
  admin: {
    title: 'Administration',
    users: 'Users and roles',
    groups: 'Visibility groups',
    integrations: 'Integrations',
    exceptions: 'Exceptions',
    imports: 'Jira import',
    audit: 'Audit log',
    operations: 'Operations',
    roles: 'Roles',
    capabilities: 'Capabilities',
    status: 'Status',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    lastLogin: 'Last sign-in',
    changeAccess: 'Change access',
    reasonRequired: 'A reason is recorded in the audit log.',
    reason: { title: 'Record a reason', label: 'Reason', hint: 'The reason is stored in the audit log.', confirm: 'Confirm' },
    integ: { lastSuccess: 'Last success', lastAttempt: 'Last attempt', lastError: 'Last error', circuit: 'Circuit open until', cursor: 'Cursor', probe: 'Probe', reconcile: 'Reconcile', reconcileQueued: 'Reconciliation queued', liveVerified: 'Live verified', health: { HEALTHY: 'Healthy', DEGRADED: 'Degraded', FAILING: 'Failing', DISABLED: 'Disabled', UNVERIFIED: 'Unverified' }, modes: { live: 'Live', fixture: 'Fixture', disabled: 'Disabled' } },
    deliveries: { title: 'Notification deliveries', hint: 'Uncertain sends may already have reached the person. Check provider evidence before retrying.', filter: 'Delivery status', filters: { DELIVERY_UNKNOWN: 'Uncertain or failed', BOUNCED: 'Bounced or suppressed', SENT: 'Sent', ALL: 'All' }, empty: 'No deliveries need attention.', attempts: '{{count}} attempt(s)', retry: 'Retry', retryTitle: 'Retry this delivery', retryHint: 'The message may be sent again. The reason is stored in the audit log.', retried: 'Retry queued' },
    services: { title: 'Services and environments', hint: 'Approve which repositories build each service and which environments are production. Coverage uses only approved mappings.', empty: 'No services discovered yet.', repositories: 'Repositories', environments: 'Environments', buildPath: 'Build path (optional)', buildPathFor: 'Build path for {{name}}', classificationFor: 'Classification of {{name}}', noRepositories: 'No repositories mapped.', approve: 'Approve mapping', approved: 'Mapping approved', classes: { PRODUCTION: 'Production', STAGING: 'Staging', DEVELOPMENT: 'Development', PREVIEW: 'Preview', OTHER: 'Other' } },
    identities: { action: 'Linked accounts', title: 'Linked accounts', hint: 'Link a GitHub or Jira account only when you have confirmed it belongs to this person. Links are audited.', none: 'No linked accounts.', add: 'Link an account', provider: 'Provider', search: 'Search by handle or ID', candidates: 'Accounts that can be linked', historical: 'Jira history', unmapped: 'Seen {{count}} time(s)', noCandidates: 'No unlinked accounts found.', link: 'Link account', linked: 'Account linked', unlink: 'Unlink' },
    exc: { empty: 'No open exceptions.', resolve: 'Resolve', ignore: 'Ignore', open: 'Open', occurrences: '{{count}} occurrence(s)', filterKind: 'Kind', all: 'All kinds', kinds: { UNMATCHED_WORK: 'Unmatched work', UNCERTAIN_ASSOCIATION: 'Uncertain association', UNMAPPED_IDENTITY: 'Unmapped identity', UNMAPPED_REPOSITORY: 'Unmapped repository', UNMAPPED_SERVICE: 'Unmapped service', CONNECTOR_FAILURE: 'Connector failure', DUPLICATE_CORRECTION: 'Duplicate correction', WEBHOOK_INTEGRITY: 'Webhook integrity', DEPLOYMENT_ORDERING: 'Deployment ordering', DELIVERY_UNKNOWN: 'Uncertain delivery', IMPORT_EXCEPTION: 'Import exception', ACL_QUARANTINE: 'Access quarantine', SCOPE_REVIEW: 'Scope review', COMPLETION_BLOCKED: 'Completion blocked' } },
    ops: { noAlerts: 'No active alerts', outbox: 'Outbox pending', worker: 'Worker', healthy: 'Healthy', stale: 'Stale', aiTokens: 'AI tokens today', queue: 'Queue', queued: 'Queued', active: 'Active', oldest: 'Oldest wait', dead: 'Dead letters', replay: 'Replay', deadEmpty: 'No dead-letter jobs.' },
    imp: { mode: 'Mode', projects: 'Project keys (comma separated; empty uses approved projects)', start: 'Start', queued: 'Import queued', resume: 'Resume', empty: 'No import runs yet.', exceptions: '{{count}} exception(s)', mapping: 'Mappings', mappingHelp: 'Run discovery first. Approve each project before sample, full or delta imports. Unmapped security levels stay quarantined for administrators.', save: 'Save mappings', saved: 'Mappings saved', approved: 'Approved', targetProject: 'Target project', targetTeam: 'Triage/responsible team', none: 'None', requestTypes: 'Issue types imported as requests', statusMap: 'Status mapping', securityLevels: 'Security levels', securityGroup: 'Visible to group', quarantine: 'Quarantine (administrators only)', reconciliation: 'Reconciliation', modes: { DISCOVERY: 'Discovery', DRY_RUN: 'Dry run', SAMPLE: 'Sample', FULL: 'Full import', DELTA: 'Delta', RECONCILE: 'Reconcile' }, statuses: { QUEUED: 'Queued', RUNNING: 'Running', PAUSED: 'Paused', SUCCEEDED: 'Succeeded', FAILED: 'Failed', CANCELED: 'Canceled' } },
    slackDest: { title: 'Slack progress destinations', help: 'Approved internal channels that receive progress for a project or team. Restricted items post only a link unless the destination is approved for restricted content and every member is a full workspace member.', scope: 'Scope', project: 'Project', team: 'Team', workspace: 'Workspace ID', channel: 'Channel ID', restricted: 'Approved for restricted content', milestones: 'Milestones', add: 'Save destination', remove: 'Remove', empty: 'No destinations configured.', saved: 'Destination saved', milestoneNames: { REQUEST_RECEIVED: 'Request received', REQUEST_ACCEPTED: 'Request accepted', REQUEST_REJECTED: 'Request rejected', REQUEST_RESOLVED: 'Request resolved', REQUEST_NEEDS_ATTENTION: 'Needs attention', WORK_STARTED: 'Work started', WORK_BLOCKED: 'Work blocked', WORK_DELIVERED: 'Work delivered' } },
  },
  reports: {
    notes: {
      sourceNotConnected: '{{source}} is not connected; values from it are unknown, not zero.',
      sourceNotLive: '{{source}} data comes from {{mode}} mode, not live evidence.',
      sourceNeverSynced: '{{source}} has not synchronized successfully yet.',
      duplicatesIncluded: '{{count}} of these requests are marked as duplicates.',
      reconsideredExcluded: '{{count}} reconsidered decisions in the period are excluded from the rate.',
      generationsAcrossRequests: '{{generations}} fulfillment generations across {{requests}} distinct requests.',
      mixedBases: 'Mixed completion bases ({{bases}}); choose one to compare like with like.',
      partialCohorts: 'The follow-up window runs from each first completion until now; recent cohorts are partial.',
      currentState: 'Current state at query time; past snapshots are not reconstructed for this metric.',
      rolledBack: '{{count}} rolled-back deployments are labeled separately.',
      noIncidentSource: 'No incident source is connected; recovery time is unavailable and never inferred from deployments.',
      activityNotProductivity: 'Contributions are activity indicators, not a productivity score.',
      prAttribution: 'Pull request authorship is labeled per pull request, not per line. Generated-file exclusions apply only with complete file data.',
      captureDenominator: 'Denominator: observed pull requests in repositories attached to projects; work outside the platform has no denominator.',
      providerErrors: '{{count}} provider calls failed.',
    },
    filters: { any: 'Any', source: 'Source', mine: 'Only my contributions' },
    chartLabel: 'Chart of {{metric}} by group',
    title: 'Reports',
    subtitle: 'Metrics computed from recorded facts, with your access applied. Unknown data is never shown as zero.',
    metric: 'Metric',
    period: 'Period',
    groupBy: 'Group by',
    basis: 'Completion basis',
    anyBasis: 'All bases',
    firstOnly: 'First completion only',
    run: 'Run',
    export: 'Export CSV',
    exporting: 'Preparing export…',
    download: 'Download CSV',
    total: 'Total',
    count: 'Count',
    unavailable: 'Unavailable',
    median: 'Median',
    p85: '85th percentile',
    coverage: 'Coverage',
    overlapping: 'Groups overlap: one task can appear in several groups, so rows do not add up to the total.',
    empty: 'No data in this period.',
    tierLabel: 'Tier',
    formula: 'Formula v{{version}}',
    watermark: 'Data as of {{time}}',
    definition: 'Definition',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    last90: 'Last 90 days',
    thisYear: 'This year',
    from: 'From',
    to: 'To',
    group: 'Group',
    value: 'Value',
    ratio: '{{num}} of {{den}}',
    tiers: { GENERAL: 'General', TECHNICAL: 'Technical', SENSITIVE: 'Sensitive' },
    coverageStatus: { COMPLETE: 'Complete', PARTIAL: 'Partial', UNKNOWN: 'Unknown' },
    groups: { none: 'No grouping', team: 'Team', project: 'Project', type: 'Type', priority: 'Priority', week: 'Week', month: 'Month', repository: 'Repository', service: 'Service', person: 'Person', outcome: 'Outcome', source: 'Source' },
    metrics: {
      requests_created: { name: 'Requests created', help: 'Submitted requests by submission date.' },
      request_first_response: { name: 'First response time', help: 'From submission to the first human triage decision.' },
      request_acceptance_rate: { name: 'Acceptance rate', help: 'First accepted decisions over accepted plus rejected.' },
      requests_fulfilled: { name: 'Requests resolved', help: 'Distinct requests with a resolution in the period.' },
      request_lead_time: { name: 'Request lead time', help: 'From submission to resolution, per resolution.' },
      work_completed: { name: 'Work completed', help: 'Distinct work items completed in the period.' },
      work_cycle_time: { name: 'Cycle time', help: 'From start to completion of each episode.' },
      work_in_progress: { name: 'Work in progress', help: 'Items in progress, review or integrated right now.' },
      blocked_time: { name: 'Blocked time', help: 'Union of blocker intervals per item in the period.' },
      review_wait: { name: 'Review wait', help: 'From review request to review decision.' },
      integration_to_production: { name: 'Integration to production', help: 'From integration to verified production delivery.' },
      deployment_frequency: { name: 'Deployment frequency', help: 'Successful production deployments.' },
      deployment_failure_rate: { name: 'Deployment failure rate', help: 'Failed over successful plus failed production attempts.' },
      reopen_rate: { name: 'Reopen rate', help: 'Completed work that was later reopened.' },
      recovery_time: { name: 'Recovery time', help: 'Needs incident evidence; unavailable until a source exists.' },
      contribution_activity: { name: 'Contributions', help: 'Observed implementation, review and other contributions.' },
      code_additions: { name: 'Lines added', help: 'Provider-reported additions in merged PRs.' },
      code_deletions: { name: 'Lines deleted', help: 'Provider-reported deletions in merged PRs.' },
      capture_coverage: { name: 'Capture coverage', help: 'Observed PRs linked to confirmed work.' },
      requester_confirmation: { name: 'Requester confirmation', help: 'Confirmed, persists or unanswered per resolution.' },
      integrated_ai_usage: { name: 'AI usage', help: 'Provider-reported tokens for translation and intake.' },
    },
  },
  attachments: {
    title: 'Files',
    add: 'Attach files',
    empty: 'No files attached yet.',
    uploading: 'Uploading {{name}}…',
    scanning: 'Scanning for malware',
    rejected: 'Rejected',
    download: 'Download',
    remove: 'Remove file',
    removed: 'File removed',
    tooLarge: '{{name}} is larger than {{max}} MiB.',
    tooMany: 'You can attach at most {{max}} files at once.',
    uploaded: '{{count}} file(s) uploaded; they become available after the malware scan.',
    pendingNote: 'Files stay quarantined until the scan finishes.',
  },
  savedViews: {
    title: 'Views',
    mine: 'My views',
    sharedWithEveryone: 'Shared views',
    shared: 'Shared',
    empty: 'No saved views yet.',
    saveCurrent: 'Save current filters…',
    name: 'View name',
    shareLabel: 'Share with everyone (filters only; each person still sees only what they can access)',
    hint: 'A saved view stores the filters, never the results.',
    saved: 'View saved',
    deleted: 'View deleted',
    delete: 'Delete view',
  },
  slack: {
    modalTitle: 'Create Vammo request',
    reviewTitle: 'Review request',
    next: 'Next',
    submit: 'Submit',
    cancel: 'Cancel',
    close: 'Close',
    privacyNote: 'Only you see this form. Your request is private until you submit it.',
    fields: { title: 'Short title', problem: 'What is happening?', expected: 'What should happen instead?', impact: 'Who is affected and how much? (leave empty if unknown)' },
    impactUnknown: 'Unknown for now',
    duplicates: 'Possibly related requests',
    submitted: 'Request *{{key}}* submitted. Follow it here: {{url}}',
    linkPrompt: 'To create requests from Slack, link your Slack account to your Vammo account (private link, valid for 10 minutes): {{url}}',
    notLinked: 'Your Slack account is not linked yet. Run /vammo-work again.',
    expired: 'This draft is no longer available. Start again with /vammo-work.',
    required: 'Required',
    unavailable: 'The request form could not be opened. Try again or use the web app.',
    progress: '*{{key}}* {{title}} — {{status}}\n{{url}}',
    progressLinkOnly: 'A tracked item was updated. Open it in Vammo Issue Tracker: {{url}}',
    linkTitle: 'Link Slack account',
    linkBody: 'Link the Slack user {{handle}} to your Vammo account? Requests you create from Slack will be filed as you.',
    linkConfirm: 'Link account',
    linkDone: 'Your Slack account is linked. You can close this tab and run /vammo-work again.',
    linkInvalid: 'This link expired or was already used. Run /vammo-work in Slack to get a new one.',
  },
  notifications: {
    preferences: {
      title: 'Notification settings',
      event: 'Event',
      email: 'Email',
      browser: 'Browser',
      immediate: 'Immediately',
      digest: 'Daily digest',
      muted: 'Off',
      on: 'On',
      off: 'Off',
    },
    events: {
      REQUEST_RECEIVED: 'Your request was received',
      REQUEST_NEEDS_INFORMATION: 'More information is needed',
      REQUEST_ACCEPTED: 'Your request was accepted',
      REQUEST_REJECTED: 'Your request was not accepted',
      REQUEST_WORK_STARTED: 'Work started on your request',
      REQUEST_RESOLVED: 'Your request was resolved',
      REQUEST_NEEDS_ATTENTION: 'A request needs attention',
      WORK_ASSIGNED: 'Work assigned to you',
      WORK_BLOCKED: 'Work you follow is blocked',
      COMMENT_ADDED: 'New comment on something you follow',
      MENTIONED: 'You were mentioned',
    },
    browser: {
      title: 'Browser notifications',
      enable: 'Turn on in this browser',
      disable: 'Turn off in this browser',
      test: 'Send a test',
      enabled: 'Browser notifications are on',
      disabled: 'Browser notifications are off in this browser',
      testSent: 'Test notification sent',
      testNone: 'No browser accepted the test. Turn notifications off and on again.',
      blockedHelp: 'Notifications are blocked for this site. Allow them in the browser’s site settings (the icon next to the address).',
      devices: 'Browsers that receive notifications',
      unknownDevice: 'Browser',
      forget: 'Forget',
      state: {
        unsupported: 'This browser cannot show notifications. On iPhone and iPad, add the tracker to the Home Screen first.',
        blocked: 'Notifications are blocked for this site. Allow them in the browser’s site settings, then reload.',
        off: 'Get assignments, mentions and updates on what you follow, even when the tracker is closed.',
        on: 'This browser shows your notifications.',
      },
    },
  },
  push: {
    testTitle: 'Notifications are on',
    testBody: 'This browser will show your Vammo Issue Tracker notifications.',
  },
  email: {
    greeting: 'Hello {{name}},',
    open: 'Open in Vammo Issue Tracker',
    footer: 'You receive this because you are involved in this record. Change notification settings in your profile.',
    actionConfirm: 'Yes, it is resolved',
    actionPersists: 'No, it is still happening',
    actionComment: 'Leave a comment',
    confirmHint: 'Let the team know whether it works for you: open the request and choose "Yes, it is resolved" or "No, it is still happening". Opening this email or link never records an answer by itself.',
    digestSubject: 'Your Vammo Issue Tracker digest',
    events: {
      REQUEST_RECEIVED: { subject: '[{{key}}] We received your request', body: 'Your request "{{title}}" was received and is waiting for triage.' },
      REQUEST_NEEDS_INFORMATION: { subject: '[{{key}}] More information needed', body: 'The team needs more information about "{{title}}".' },
      REQUEST_ACCEPTED: { subject: '[{{key}}] Your request was accepted', body: 'Your request "{{title}}" was accepted. Acceptance is not a delivery date; you will hear when work starts.' },
      REQUEST_REJECTED: { subject: '[{{key}}] Your request was not accepted', body: 'Your request "{{title}}" was not accepted. Open it to read the reason.' },
      REQUEST_WORK_STARTED: { subject: '[{{key}}] Work started', body: 'Work has started on "{{title}}".' },
      REQUEST_RESOLVED: { subject: '[{{key}}] Resolved — please confirm', body: 'The team resolved "{{title}}".' },
      REQUEST_NEEDS_ATTENTION: { subject: '[{{key}}] Needs attention', body: 'The requester reports that "{{title}}" still happens, or required delivery was lost.' },
      WORK_ASSIGNED: { subject: '[{{key}}] Assigned to you', body: '"{{title}}" was assigned to you.' },
      WORK_BLOCKED: { subject: '[{{key}}] Blocked', body: '"{{title}}" is blocked.' },
      WORK_DONE: { subject: '[{{key}}] Done', body: '"{{title}}" is done.' },
      COMMENT_ADDED: { subject: '[{{key}}] New comment', body: 'There is a new comment on "{{title}}".' },
      MENTIONED: { subject: '[{{key}}] You were mentioned', body: 'You were mentioned on "{{title}}".' },
    },
  },
  time: {
    justNow: 'just now',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };
/** Every locale must provide exactly the English structure. */
export type Messages = Widen<typeof en>;
